import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  CreateVaultSyncRequest,
  VaultSync,
  VaultSyncProviderId,
  VaultSyncResponseSchema,
  VaultSyncStatus,
  VaultSyncsResponseSchema,
} from "@forgepoint/shared";

/** Display name for each Vault sync source. */
export const VAULT_SYNC_PROVIDER_LABELS: Record<VaultSyncProviderId, string> = {
  microsoft_onedrive: "OneDrive",
  sharepoint: "SharePoint",
  box: "Box",
};

/** What a synced folder's status badge shows, in precedence order. */
export type VaultSyncDisplayState = "paused" | "error" | "syncing" | "synced";

/**
 * Derives the badge state of a sync: paused, then error, then syncing, then synced.
 * @param sync - The Vault sync
 * @returns The state to display for the sync
 */
export function getVaultSyncDisplayState(
  sync: VaultSync,
): VaultSyncDisplayState {
  if (sync.status === "DISABLED") {
    return "paused";
  }
  if (sync.progress?.lastError) {
    return "error";
  }
  if (!sync.progress?.lastSuccessAt || sync.progress.inProgress) {
    return "syncing";
  }
  return "synced";
}

/** Per-sync pause/resume and unlink flags, keyed by sync id. */
export interface VaultSyncRequestState {
  isLoadingUpdate: boolean;
  isLoadingUnlink: boolean;
  error: string | null;
}

const INITIAL_REQUEST_STATE: VaultSyncRequestState = {
  isLoadingUpdate: false,
  isLoadingUnlink: false,
  error: null,
};

interface VaultSyncsStore {
  syncs: Record<string, VaultSync>;
  byOrganizationFileId: Record<string, string>;
  status: Record<string, VaultSyncRequestState>;
  isLoadingList: boolean;
  listError: string | null;
  isCreating: boolean;
  createError: string | null;

  fetchSyncs: () => Promise<void>;
  createSync: (request: CreateVaultSyncRequest) => Promise<VaultSync | null>;
  updateSyncStatus: (
    id: string,
    status: VaultSyncStatus,
  ) => Promise<VaultSync | null>;
  unlink: (id: string) => Promise<boolean>;
  clearCreateError: () => void;
}

/** Vault folders linked to cloud drives, keyed by sync id. */
export const useVaultSyncsStore = create<VaultSyncsStore>((set) => {
  // A list response is stale once a newer list request starts or a mutation lands after it was issued.
  let latestListRequest = 0;
  let mutationVersion = 0;

  const setRequestState = (id: string, patch: Partial<VaultSyncRequestState>) =>
    set((state) => ({
      status: {
        ...state.status,
        [id]: { ...(state.status[id] ?? INITIAL_REQUEST_STATE), ...patch },
      },
    }));

  const upsertSync = (sync: VaultSync) =>
    set((state) => {
      const syncs = { ...state.syncs, [sync.id]: sync };
      return { syncs, byOrganizationFileId: indexByOrganizationFileId(syncs) };
    });

  return {
    syncs: {},
    byOrganizationFileId: {},
    status: {},
    isLoadingList: false,
    listError: null,
    isCreating: false,
    createError: null,

    fetchSyncs: async () => {
      latestListRequest += 1;
      const request = latestListRequest;
      const version = mutationVersion;
      set({ isLoadingList: true, listError: null });
      const response = await requestVaultSyncs(
        "organization-files/syncs",
        "GET",
      );
      if (request !== latestListRequest) {
        return;
      }
      if (version !== mutationVersion) {
        set({ isLoadingList: false });
        return;
      }

      const parsed = VaultSyncsResponseSchema.safeParse(response.data);
      if (!response.ok || !parsed.success) {
        set({
          isLoadingList: false,
          listError: response.error ?? "Failed to load synced folders",
        });
        return;
      }

      const syncs = Object.fromEntries(
        parsed.data.syncs.map((sync) => [sync.id, sync]),
      );
      set({
        syncs,
        byOrganizationFileId: indexByOrganizationFileId(syncs),
        isLoadingList: false,
      });
    },

    createSync: async (request) => {
      set({ isCreating: true, createError: null });
      const response = await requestVaultSyncs(
        "organization-files/syncs",
        "POST",
        request,
      );
      const parsed = VaultSyncResponseSchema.safeParse(response.data);
      if (!response.ok || !parsed.success) {
        set({
          isCreating: false,
          createError: response.error ?? "Failed to add synced folder",
        });
        return null;
      }

      mutationVersion += 1;
      upsertSync(parsed.data.sync);
      set({ isCreating: false });
      return parsed.data.sync;
    },

    updateSyncStatus: async (id, status) => {
      setRequestState(id, { isLoadingUpdate: true, error: null });
      const response = await requestVaultSyncs(
        `organization-files/syncs/${id}`,
        "PUT",
        { status },
      );
      const parsed = VaultSyncResponseSchema.safeParse(response.data);
      if (!response.ok || !parsed.success) {
        setRequestState(id, {
          isLoadingUpdate: false,
          error: response.error ?? "Failed to update synced folder",
        });
        return null;
      }

      mutationVersion += 1;
      upsertSync(parsed.data.sync);
      setRequestState(id, { isLoadingUpdate: false });
      return parsed.data.sync;
    },

    unlink: async (id) => {
      setRequestState(id, { isLoadingUnlink: true, error: null });
      const response = await requestVaultSyncs(
        `organization-files/syncs/${id}`,
        "DELETE",
      );
      if (!response.ok) {
        setRequestState(id, {
          isLoadingUnlink: false,
          error: response.error ?? "Failed to unlink synced folder",
        });
        return false;
      }

      mutationVersion += 1;
      set((state) => {
        const syncs = Object.fromEntries(
          Object.entries(state.syncs).filter(([syncId]) => syncId !== id),
        );
        const status = Object.fromEntries(
          Object.entries(state.status).filter(([syncId]) => syncId !== id),
        );
        return {
          syncs,
          status,
          byOrganizationFileId: indexByOrganizationFileId(syncs),
        };
      });
      return true;
    },

    clearCreateError: () => set({ createError: null }),
  };
});

/**
 * Calls a Vault sync route, reporting a thrown network error as a failed response.
 * @param path - The API path
 * @param method - The HTTP method
 * @param body - The optional JSON body
 * @returns The fetchApi result, or a failed result when the request threw
 */
async function requestVaultSyncs(
  path: string,
  method: "GET" | "POST" | "PUT" | "DELETE",
  body?: unknown,
): ReturnType<typeof fetchApi> {
  try {
    return await fetchApi(
      path,
      method,
      body === undefined ? undefined : { body },
    );
  } catch (error) {
    captureException(
      error instanceof Error ? error : new Error("Vault sync request failed"),
    );
    return { ok: false, status: 0, data: undefined, error: null };
  }
}

/**
 * Maps each synced Vault folder id to its sync id.
 * @param syncs - Syncs keyed by id
 * @returns Sync ids keyed by organization file id
 */
function indexByOrganizationFileId(
  syncs: Record<string, VaultSync>,
): Record<string, string> {
  return Object.fromEntries(
    Object.values(syncs).map((sync) => [sync.organizationFileId, sync.id]),
  );
}
